import React, { useContext, useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useNavigate, useParams } from 'react-router-dom';
import TopBar from '../components/TopBar';
import ResolvedStorageImage from '../components/ResolvedStorageImage';
import { UserContext } from '../App';
import { db, functions } from '../firebase';
import layoutStyles from '../styles/layout.module.css';
import styles from './DropDetail.module.css';
import { formatCountdown, formatDropDateTime, formatDropStatus, getDropStatus, formatPrice } from '../utils/marketplace';
import { getCompactCollectibleImagePath } from '../utils/collectibleMedia';

export default function DropDetail() {
  const { dropId } = useParams();
  const appUser = useContext(UserContext);
  const navigate = useNavigate();
  const [drop, setDrop] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [nowMs, setNowMs] = useState(Date.now());
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const dropSnap = await getDoc(doc(db, 'drops', String(dropId)));
        if (!dropSnap.exists()) throw new Error('Drop not found.');
        const dropData = { id: dropSnap.id, ...(dropSnap.data() || {}) };
        if (String(dropData.artistOwnerUid || '') !== String(appUser?.id || '')) throw new Error('Only the artist owner can view this drop.');
        const productSnap = dropData.productId ? await getDoc(doc(db, 'products', String(dropData.productId))) : null;
        if (!active) return;
        setDrop(dropData);
        setProduct(productSnap?.exists() ? { id: productSnap.id, ...(productSnap.data() || {}) } : null);
        setError('');
      } catch (err) {
        if (active) setError(err?.message || 'Unable to load drop.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [appUser?.id, dropId]);

  const status = useMemo(() => formatDropStatus(drop || {}, nowMs), [drop, nowMs]);
  const rawStatus = useMemo(() => getDropStatus(drop || {}, nowMs), [drop, nowMs]);
  const countdown = useMemo(() => {
    if (!drop) return null;
    const currentStatus = getDropStatus(drop, nowMs);
    const saleStartMs = drop.saleStartAt?.toMillis?.() || 0;
    const saleEndMs = drop.saleEndAt?.toMillis?.() || 0;
    if (currentStatus === 'scheduled' && saleStartMs > nowMs) return { label: 'Goes live in', targetMs: saleStartMs };
    if (currentStatus === 'active' && saleEndMs > nowMs) return { label: 'Ends in', targetMs: saleEndMs };
    return null;
  }, [drop, nowMs]);

  const handleCancel = async () => {
    try {
      setBusy(true);
      setError('');
      const callable = httpsCallable(functions, 'cancelDrop');
      await callable({ dropId });
      const refreshed = await getDoc(doc(db, 'drops', String(dropId)));
      setDrop(refreshed.exists() ? { id: refreshed.id, ...(refreshed.data() || {}) } : null);
      setConfirmCancelOpen(false);
    } catch (err) {
      setError(err?.message || 'Unable to cancel drop.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={layoutStyles.detailPage}>
      {confirmCancelOpen ? (
        <div className={styles.confirmOverlay} role="dialog" aria-modal="true" aria-labelledby="cancel-drop-title">
          <div className={styles.confirmDialog}>
            <h2 id="cancel-drop-title">Cancel this drop?</h2>
            <p>This will stop sales for the published product immediately. This action cannot be undone.</p>
            <div className={styles.confirmActions}>
              <button type="button" className={styles.confirmSecondary} onClick={() => setConfirmCancelOpen(false)} disabled={busy}>Keep Drop</button>
              <button type="button" className={styles.cancelButton} onClick={handleCancel} disabled={busy}>{busy ? 'Canceling…' : 'Yes, Cancel Drop'}</button>
            </div>
          </div>
        </div>
      ) : null}
      <TopBar variant="back" backLabel="Back" onBack={() => navigate('/creator/products')} />
      <div className={`${layoutStyles.detailMain} ${styles.page}`.trim()}>
        {loading ? <p className={styles.state}>Loading drop…</p> : null}
        {error ? <p className={styles.error}>{error}</p> : null}
        {!loading && drop ? (
          <>
            <header className={styles.hero}>
              <div>
                <span className={styles.eyebrow}>Drop</span>
                <h1>{drop.title || 'Untitled Drop'}</h1>
                <p>{drop.description || 'No description added yet.'}</p>
              </div>
              <div className={styles.heroMeta}>
                <span className={styles.statusPill}>{status}</span>
                {countdown ? (
                  <div className={styles.countdownCard}>
                    <span>{countdown.label}</span>
                    <strong>{formatCountdown(countdown.targetMs, nowMs)}</strong>
                  </div>
                ) : null}
              </div>
            </header>

            <section className={styles.summaryGrid}>
              <article className={styles.summaryCard}><span>Starts</span><strong>{formatDropDateTime(drop, 'saleStartAt')}</strong></article>
              <article className={styles.summaryCard}><span>Ends</span><strong>{formatDropDateTime(drop, 'saleEndAt')}</strong></article>
              <article className={styles.summaryCard}><span>Supply</span><strong>{Number(drop.maxSupply || 0)}</strong></article>
              <article className={styles.summaryCard}><span>Price</span><strong>{formatPrice(drop)}</strong></article>
            </section>

            {rawStatus !== 'draft' && rawStatus !== 'canceled' && rawStatus !== 'ended' ? (
              <section className={styles.actionCard}>
                <div>
                  <h2>Emergency stop</h2>
                  <p>Canceling this drop immediately stops the published sale.</p>
                </div>
                <button type="button" className={styles.cancelButton} onClick={() => setConfirmCancelOpen(true)} disabled={busy}>Cancel Drop</button>
              </section>
            ) : null}

            <section className={styles.catalog}>
              <div className={styles.sectionHeader}>
                <h2>Published item</h2>
                <p>This drop publishes one product with a single timed release window.</p>
              </div>
              <div className={styles.grid}>
                <article className={styles.card}>
                  <div className={styles.media}>
                    <ResolvedStorageImage
                      path={getCompactCollectibleImagePath(product || { media: drop.media || [] })}
                      alt={drop.title || 'Drop'}
                      fallback={<div className={styles.placeholder}>Drop</div>}
                    />
                  </div>
                  <div className={styles.cardBody}>
                    <span className={styles.cardType}>Single Drop</span>
                    <h3>{product?.title || drop.title || drop.id}</h3>
                    <p>{product?.description || drop.description || 'No description added yet.'}</p>
                    <div className={styles.cardMeta}>
                      <span>Price <strong>{formatPrice(product || drop)}</strong></span>
                      <span>Supply <strong>{Number(product?.maxSupply || drop.maxSupply || 0)}</strong></span>
                      <span>Status <strong>{status}</strong></span>
                    </div>
                    {product?.id ? <button type="button" className={styles.openButton} onClick={() => navigate(`/products/${product.id}`, { state: { backTo: `/creator/drops/${dropId}` } })}>Open product</button> : null}
                  </div>
                </article>
              </div>
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
}
